import type { Registry as Services } from '@ember/service';
import type { Dayjs } from 'dayjs';
import { tKey } from 'ember-intl';
import type { GeneralDatepickersDatepickerWithSidebarSignature } from 'lumen/components/general/datepickers/datepicker-with-sidebar';
import {
  GROUP_BY,
  type GroupBy,
} from 'lumen/utils/constants/market-snapshots-constants';

export const DEFAULT_PAGE_SIZE = 25;
export const PAGE_SIZE_OPTIONS = [25, 50, 100, 250];

const DATE_FORMAT = 'YYYY-MM-DD';

/** Current month, used when the URL has no `fromDate` / `toDate`. */
export function getDefaultDateRange(today: Dayjs): {
  fromDate: string;
  toDate: string;
} {
  return {
    fromDate: today.startOf('month').format(DATE_FORMAT),
    toDate: today.endOf('month').format(DATE_FORMAT),
  };
}

/** Datepicker presets; month view offers whole-month ranges only. */
export function buildSnapshotSidebarLists(
  today: Dayjs,
  intl: Services['intl'],
  groupBy: GroupBy,
): GeneralDatepickersDatepickerWithSidebarSignature['Args']['sidebarLists'] {
  const thisMonth = {
    id: 'this-month',
    text: tKey('general.this_month'),
    range: { start: today.startOf('month'), end: today.endOf('month') },
  };
  const thisYear = {
    id: 'this-year',
    text: tKey('general.this_year'),
    range: { start: today.startOf('year'), end: today.endOf('year') },
  };
  const custom = {
    id: 'custom',
    text: tKey('general.custom'),
    range: { start: today.startOf('month'), end: today.endOf('month') },
  };

  const options =
    groupBy === GROUP_BY.MONTH
      ? [
          thisMonth,
          {
            id: 'next-12-months',
            text: tKey('general.next_12_months'),
            range: {
              start: today.startOf('month'),
              end: today.add(11, 'month').endOf('month'),
            },
          },
          thisYear,
          custom,
        ]
      : [
          {
            id: 'today',
            text: tKey('general.today'),
            range: { start: today, end: today },
          },
          {
            id: 'yesterday',
            text: tKey('general.yesterday'),
            range: {
              start: today.subtract(1, 'day'),
              end: today.subtract(1, 'day'),
            },
          },
          {
            id: 'this-week',
            text: tKey('general.this_week'),
            range: { start: today.startOf('week'), end: today.endOf('week') },
          },
          thisMonth,
          thisYear,
          custom,
        ];

  return [{ title: intl.t(tKey('general.selected_period')), options }];
}
